import { Avatar, Icon, Loading, Typography } from "@/components/atoms";
import { FleetDriver } from "@/redux/slices/driver/types";
import { copyToClipboard, getAssetUrl } from "@/utils";

const getInitials = (name: string) =>
  name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

interface LoginCardProps {
  heading: string;
  name?: string;
  role: string;
  emailLabel: string;
  email?: string;
  avatar?: React.ReactNode;
}

const LoginCard = ({ heading, name, role, emailLabel, email, avatar }: LoginCardProps) => (
  <div className="rounded-lg border border-neutral-mid-gray p-4">
    <Typography variant="small" className="mb-3 uppercase text-neutral-silver-gray">
      {heading}
    </Typography>
    <div className="mb-3 flex items-center gap-3">
      {avatar}
      <div className="flex flex-col">
        <Typography variant="action" className="text-black">
          {name}
        </Typography>
        <Typography variant="small-regular" className="text-neutral-silver-gray">
          {role}
        </Typography>
      </div>
    </div>
    <div className="flex items-center justify-between gap-2 rounded-lg border border-neutral-mid-gray bg-neutral-surface-gray px-3 py-2">
      <div className="min-w-0">
        <Typography variant="small" className="uppercase text-neutral-silver-gray">
          {emailLabel}
        </Typography>
        <Typography variant="paragraph" className="truncate text-black">
          {email}
        </Typography>
      </div>
      <button
        onClick={() => copyToClipboard(email ?? "")}
        className="shrink-0 p-1 text-neutral-dark-gray hover:text-neutral-black"
        aria-label={`Copy ${emailLabel.toLowerCase()}`}
      >
        <Icon name="Copy" size="lg" />
      </button>
    </div>
  </div>
);

export interface AllSetCardProps {
  ownerName?: string;
  ownerEmail?: string;
  driver?: FleetDriver;
  isLoading?: boolean;
}

export const AllSetCard = ({ ownerName, ownerEmail, driver, isLoading }: AllSetCardProps) => (
  <div className="relative rounded-lg border border-neutral-mid-gray bg-white p-6">
    {isLoading && <Loading className="rounded-lg" />}
    <div className="mb-4 flex items-center gap-3">
      <div className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-success bg-success-light">
        <span className="text-2xl">🎉</span>
      </div>
      <div className="flex items-center gap-2">
        <Typography variant="title" className="text-black">
          You&apos;re all set!
        </Typography>
        <Icon isCustom name="check" size="md" className="text-success" />
      </div>
    </div>
    <Typography variant="paragraph" className="mb-5 text-neutral-dark-gray">
      Your account is fully configured. You can now accept bookings and get paid through the RideMinder Driver App.
    </Typography>
    <div className="grid gap-3 md:grid-cols-2">
      <LoginCard
        heading="Operator Web Portal"
        name={ownerName}
        role="Owner"
        emailLabel="Web App Login Email"
        email={ownerEmail}
        avatar={
          <Avatar size="md" className="bg-primary text-white">
            <Typography variant="action">{ownerName ? getInitials(ownerName) : ""}</Typography>
          </Avatar>
        }
      />
      {driver && (
        <LoginCard
          heading="Driver App"
          name={driver.name}
          role="Driver"
          emailLabel="Driver Login Email"
          email={driver.email}
          avatar={
            <Avatar
              size="md"
              src={driver.avatar ? getAssetUrl(driver.avatar) : undefined}
              className="bg-neutral-mid-gray text-neutral-dark-gray"
            >
              {!driver.avatar && <Typography variant="action">{driver.name ? getInitials(driver.name) : ""}</Typography>}
            </Avatar>
          }
        />
      )}
    </div>
  </div>
);
